import {
  QuillEditor as BaseQuillEditor,
  QuillEditorToolbar,
} from '@/components_deprecated'
import { urlIsValid } from '@/utils/parsing'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type Quill from 'quill'
import type { QuillOptions } from 'quill'
import React, { useImperativeHandle, useRef } from 'react'
import styled from 'styled-components'

const Wrapper = styled.div``
const QuillEditor = styled(BaseQuillEditor)`
  min-height: 72px;
  border: 0;
  background: rgba(255, 255, 255, 0) !important;

  .ql-container {
    min-height: 72px;
    padding: 1rem !important;
    background: inherit !important;

    .ql-editor {
      min-height: 1ch;
      padding: 0 !important;
      border: 0;
      background: inherit !important;

      &::before {
        font-style: normal;
        color: ${props => props.theme.colors.gray5};
      }
    }
  }
`
const ToolbarWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  background: ${props => props.theme.colors.quaternary};
  border-bottom: 1px solid ${props => props.theme.colors.gray10};
`

export interface EntryQuillEditorRef {
  quill: Quill | null
}

interface EntryQuillEditorProps {
  placeholder?: string
  error?: string | null

  hasImage?: boolean

  onInit: (quill: Quill) => void
  onChange: (content: string, quill: Quill) => void
}

export const EntryQuillEditor = React.forwardRef<
  EntryQuillEditorRef,
  EntryQuillEditorProps
>(({ placeholder, hasImage = false, onInit, onChange }, ref) => {
  const translations = useTranslate({
    addAttachment: 'common.add-attachment',
    submit: 'forum.post-comment',

    linkHelper: 'forum.quill-link-helper',
    enterUrl: 'common.enter-url',
    invalidUrl: 'common.invalid-url',
  })

  const quillRef = useRef<Quill>(null)
  const addToast = useToast()

  useImperativeHandle(ref, () => ({
    quill: quillRef.current,
  }))

  function handleQuillLink(value: boolean) {
    const quill = quillRef.current
    if (!quill) return

    const selectionLength = quill.getSelection()?.length ?? 0
    if (selectionLength === 0) {
      addToast('info', translations.linkHelper)
      return
    }

    if (value) {
      let href = window.prompt(translations.enterUrl)
      if (!href) return

      // Check if mailto or tel
      if (!/mailto:.+@.+\..+/.test(href) && !/tel:(\+)?\d+/.test(href)) {
        // Attach https if not present
        if (!/^\w+:\/\//.test(href)) href = `https://${href}`

        if (!urlIsValid(href)) {
          addToast('error', translations.invalidUrl)
          return
        }
      }

      quill.format('link', href)
    } else {
      quill.format('link', false)
    }
  }

  const quillOptions: QuillOptions = {
    bounds: '#editor',
    modules: {
      toolbar: {
        container: '#toolbar',
        handlers: {
          link: handleQuillLink,
        },
      },
      uploader: {
        handler: () => void 0,
      },
    },
  }

  return (
    <Wrapper>
      <ToolbarWrapper>
        <QuillEditorToolbar hasImages={hasImage} />
      </ToolbarWrapper>

      <QuillEditor
        id="editor"
        placeholder={placeholder}
        outerQuillRef={quillRef}
        options={quillOptions}
        hasImages
        onChange={onChange}
        onInit={onInit}
      />
    </Wrapper>
  )
})
